import { useCallback, useEffect, useState } from 'react';
import { csrfFetch } from '@/lib/csrf';
import { logError } from '@/lib/logger';
import { GENERIC_ERROR_MESSAGE } from '../constants';
import type { EmoviDocConversation } from '../types';

type UseEmoviDocTitleParams = {
  patientId: number;
  conversationId: number | null;
  initialTitle: string;
};

type UseEmoviDocTitleReturn = {
  titleDraft: string;
  setTitleDraft: (value: string) => void;
  isEditing: boolean;
  setIsEditing: (value: boolean) => void;
  isSaving: boolean;
  save: () => Promise<EmoviDocConversation | null>;
};

export function useEmoviDocTitle({
  patientId,
  conversationId,
  initialTitle,
}: UseEmoviDocTitleParams): UseEmoviDocTitleReturn {
  const [titleDraft, setTitleDraft] = useState(initialTitle);
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (!isEditing) {
      setTitleDraft(initialTitle);
    }
  }, [initialTitle, isEditing]);

  const save = useCallback(async (): Promise<EmoviDocConversation | null> => {
    if (!conversationId) return null;

    setIsSaving(true);
    try {
      const response = await csrfFetch(
        `/therapists/patients/${patientId}/emovi_doc/${conversationId}`,
        {
          method: 'PATCH',
          body: JSON.stringify({ title: titleDraft.trim() }),
        },
      );
      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      const payload = (await response.json()) as { conversation?: EmoviDocConversation };
      return payload.conversation ?? null;
    } catch (error) {
      logError('emovidoc.update_title.error', error);
      throw new Error(GENERIC_ERROR_MESSAGE);
    } finally {
      setIsSaving(false);
    }
  }, [conversationId, patientId, titleDraft]);

  return { titleDraft, setTitleDraft, isEditing, setIsEditing, isSaving, save };
}
